import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import Layout from "@/components/Layout";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Activity, CheckCircle2, Clock, Trophy, ArrowRight, Cpu, Network, GaugeCircle, FlaskConical, ClipboardList } from "lucide-react";

const moduleMeta = {
  plc_hmi: { label: "PLC + HMI", icon: Cpu },
  scada: { label: "SCADA", icon: Network },
  dcs: { label: "DCS", icon: GaugeCircle },
  lab: { label: "Lab", icon: FlaskConical },
};
const diffColor = {
  beginner: "text-status-normal border-status-normal/40 bg-status-normal/10",
  intermediate: "text-status-warning border-status-warning/40 bg-status-warning/10",
  advanced: "text-status-critical border-status-critical/40 bg-status-critical/10",
};

function Stat({ icon: Icon, label, value, accent }) {
  return (
    <div className="bg-card border border-border/60 rounded-md p-4" data-testid={`stat-${label.toLowerCase().replace(/\s/g, "-")}`}>
      <div className="flex items-center justify-between">
        <span className="label-ot">{label}</span>
        <Icon className={`w-4 h-4 ${accent}`} />
      </div>
      <div className="font-mono text-3xl font-semibold mt-2">{value}</div>
    </div>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [starting, setStarting] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/dashboard").then((r) => setData(r.data)).catch(() => toast.error("Failed to load dashboard"));
  }, []);

  const startScenario = async (scenarioId) => {
    setStarting(scenarioId);
    try {
      const r = await api.post("/attempts", { scenario_id: scenarioId });
      navigate(`/simulator/${r.data.attempt_id}`);
    } catch {
      toast.error("Could not start scenario");
      setStarting(null);
    }
  };

  if (!data) {
    return <Layout><div className="font-mono text-muted-foreground text-sm py-20 text-center animate-pulse">LOADING TELEMETRY…</div></Layout>;
  }

  const { stats, assignments, recent_attempts, scenarios } = data;
  const scMap = Object.fromEntries(scenarios.map((s) => [s.scenario_id, s]));

  return (
    <Layout>
      <div className="flex items-end justify-between flex-wrap gap-4 mb-6">
        <div>
          <div className="label-ot text-primary">Multi-Plant OT Range · Operator Console</div>
          <h1 className="font-heading text-3xl sm:text-4xl font-extrabold tracking-tight mt-1">
            Welcome back, {user?.name?.split(" ")[0]}
          </h1>
        </div>
        <Button data-testid="browse-labs-button" onClick={() => navigate("/scenarios")} className="rounded-sm font-semibold">
          Browse Lab Library <ArrowRight className="w-4 h-4 ml-2" />
        </Button>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <Stat icon={Activity} label="Total Attempts" value={stats.total_attempts} accent="text-primary" />
        <Stat icon={CheckCircle2} label="Completed" value={stats.completed} accent="text-status-normal" />
        <Stat icon={Trophy} label="Avg Score" value={stats.avg_score} accent="text-status-warning" />
        <Stat icon={Trophy} label="Best Score" value={stats.best_score} accent="text-status-warning" />
      </div>

      {/* Assignments */}
      <section className="mb-8">
        <div className="flex items-center gap-2 mb-3">
          <ClipboardList className="w-4 h-4 text-primary" />
          <h2 className="font-heading text-lg font-semibold">Assigned to you</h2>
        </div>
        {assignments.length === 0 ? (
          <div className="bg-card border border-border/60 rounded-md p-6 text-sm text-muted-foreground">
            No assignments yet. Pick any lab from the library to start training.
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {assignments.map((a) => {
              const s = a.scenario || scMap[a.scenario_id];
              const M = moduleMeta[s?.module] || moduleMeta.lab;
              return (
                <div key={a.assignment_id} data-testid="assignment-card" className="bg-card border border-border/60 rounded-md p-4 flex flex-col">
                  <div className="flex items-center justify-between">
                    <span className="flex items-center gap-1.5 text-xs font-mono text-muted-foreground"><M.icon className="w-3.5 h-3.5" />{M.label}</span>
                    <span className={`text-[10px] uppercase tracking-widest font-semibold px-2 py-0.5 rounded-sm border ${diffColor[s?.difficulty]}`}>{s?.difficulty}</span>
                  </div>
                  <h3 className="font-heading font-semibold mt-2">{s?.title}</h3>
                  <p className="text-xs text-muted-foreground mt-1 flex-1">{s?.description}</p>
                  <Button data-testid="assignment-launch-button" onClick={() => startScenario(s.scenario_id)} disabled={starting === s?.scenario_id}
                    className="rounded-sm font-semibold mt-3 w-full">
                    {starting === s?.scenario_id ? "Launching…" : "Launch"}
                  </Button>
                </div>
              );
            })}
          </div>
        )}
      </section>

      {/* Recent attempts */}
      <section>
        <h2 className="font-heading text-lg font-semibold mb-3">Recent activity</h2>
        <div className="bg-card border border-border/60 rounded-md overflow-hidden">
          {recent_attempts.length === 0 ? (
            <div className="p-6 text-sm text-muted-foreground">No attempts logged yet.</div>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border/60 text-left label-ot">
                  <th className="px-4 py-2.5 font-semibold">Scenario</th>
                  <th className="px-4 py-2.5 font-semibold hidden sm:table-cell">Module</th>
                  <th className="px-4 py-2.5 font-semibold">Status</th>
                  <th className="px-4 py-2.5 font-semibold text-right">Score</th>
                  <th className="px-4 py-2.5"></th>
                </tr>
              </thead>
              <tbody>
                {recent_attempts.map((a) => (
                  <tr key={a.attempt_id} data-testid="recent-attempt-row" className="border-b border-border/30 last:border-0 hover:bg-secondary/30">
                    <td className="px-4 py-2.5 font-medium">{a.scenario_title}</td>
                    <td className="px-4 py-2.5 hidden sm:table-cell font-mono text-xs text-muted-foreground">{(moduleMeta[a.module] || moduleMeta.lab).label}</td>
                    <td className="px-4 py-2.5">
                      {a.status === "completed" ? (
                        <span className="inline-flex items-center gap-1.5 text-xs text-status-normal"><CheckCircle2 className="w-3.5 h-3.5" />Completed</span>
                      ) : (
                        <span className="inline-flex items-center gap-1.5 text-xs text-status-warning"><Clock className="w-3.5 h-3.5" />In progress</span>
                      )}
                    </td>
                    <td className="px-4 py-2.5 text-right font-mono">{a.status === "completed" ? `${a.score}/${a.max_score}` : "—"}</td>
                    <td className="px-4 py-2.5 text-right">
                      <button
                        data-testid="resume-attempt-button"
                        onClick={() => navigate(a.status === "completed" ? `/scorecard/${a.attempt_id}` : `/simulator/${a.attempt_id}`)}
                        className="text-primary hover:underline text-xs font-medium"
                      >
                        {a.status === "completed" ? "View report" : "Resume"} →
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </section>
    </Layout>
  );
}
